import { useRef } from 'react';
import { Button, Field, Input, Select, Textarea } from '@bb/ui';
import { PersonalizationButton } from '../../../composer/shared/PersonalizationButton.tsx';
import {
  CONTEXT_EXPRESSION_MAX,
  CONTEXT_MAX_VARIABLES,
  CONTEXT_TYPE_INFO,
  CONTEXT_TYPE_OPTIONS,
  contextIssues,
  contextReference,
  hasContextIssues,
  newVariable,
  readContext,
  type ContextConfig,
  type ContextIssues,
  type ContextVariable,
  type ContextVariableType,
} from './contextConfig.ts';
import { ContextPreview } from './ContextPreview.tsx';
import './stepPanels.css';

/** A variable earlier Context steps set, with the names of those steps. */
export interface EarlierVariableView {
  name: string;
  types: ContextVariableType[];
  from: string[];
}

export interface ContextStepPanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit; the step keeps its one port (`next`). */
  onChange: (next: ContextConfig) => void;
  readOnly?: boolean;
  /** What earlier Context steps set on the way here; left out when the graph is not known. */
  earlier?: EarlierVariableView[];
  /** The employee may preview the step (`canvas.edit`). */
  canPreview?: boolean;
}

const quoted = (names: string[]) => names.map((n) => `«${n}»`).join(', ');

function Earlier({ earlier }: { earlier: EarlierVariableView[] }) {
  return (
    <section aria-label="Context from earlier steps" className="notice step-panel__summary context-step__earlier">
      <h4>Context from earlier steps</h4>
      {earlier.length === 0 ? (
        <p className="hint">No earlier Context step sets a variable on the way to this step.</p>
      ) : (
        <>
          <ul>
            {earlier.map((v) => (
              <li key={v.name}>
                <code>{contextReference(v.name)}</code> · {v.types.map((t) => CONTEXT_TYPE_INFO[t].label).join(' or ')} · from {quoted(v.from)}
              </li>
            ))}
          </ul>
          <p className="hint">The values below can use them; the Canvas entry may add more (API trigger context).</p>
        </>
      )}
    </section>
  );
}

interface VariableRowProps {
  environmentId: string;
  index: number;
  count: number;
  variable: ContextVariable;
  issues: ContextIssues['each'][number];
  /** Earlier steps that already set this name (this step then replaces their value). */
  replaces: string[] | undefined;
  readOnly: boolean;
  set: (next: ContextVariable) => void;
  move: (delta: number) => void;
  remove: () => void;
}

function VariableRow({ environmentId, index, count, variable, issues, replaces, readOnly, set, move, remove }: VariableRowProps) {
  const expression = useRef<HTMLTextAreaElement>(null);
  const title = variable.name || `variable ${index + 1}`;
  return (
    <li>
      <section aria-label={`Variable ${index + 1}: ${variable.name}`} className="step-panel__group">
        <div className="context-step__head">
          <Field label="Name" error={issues.name}>
            <Input value={variable.name} maxLength={100} spellCheck={false} autoComplete="off" disabled={readOnly} onChange={(e) => set({ ...variable, name: e.target.value })} />
          </Field>
          <Field label="Type">
            <Select options={CONTEXT_TYPE_OPTIONS} value={variable.type} disabled={readOnly} onChange={(e) => set({ ...variable, type: e.target.value as ContextVariableType })} />
          </Field>
          {!readOnly && (
            <>
              <Button size="sm" variant="ghost" aria-label={`Move variable ${title} up`} disabled={index === 0} onClick={() => move(-1)}>
                ↑
              </Button>
              <Button size="sm" variant="ghost" aria-label={`Move variable ${title} down`} disabled={index === count - 1} onClick={() => move(1)}>
                ↓
              </Button>
              <Button size="sm" variant="ghost" aria-label={`Remove variable ${title}`} disabled={count === 1} onClick={remove}>
                Remove
              </Button>
            </>
          )}
        </div>
        <div className="context-step__expression">
          <Field label="Value (Liquid)" error={issues.expression} hint={CONTEXT_TYPE_INFO[variable.type].hint}>
            <Textarea
              ref={expression}
              rows={2}
              value={variable.expression}
              maxLength={CONTEXT_EXPRESSION_MAX}
              spellCheck={false}
              placeholder="{{event_properties.${amount}}}"
              disabled={readOnly}
              onChange={(e) => set({ ...variable, expression: e.target.value })}
            />
          </Field>
          <PersonalizationButton environmentId={environmentId} target={expression} value={variable.expression} onChange={(next) => set({ ...variable, expression: next })} label={`Personalize the value of ${title}`} disabled={readOnly} />
        </div>
        {!issues.name && (
          <p className="hint">
            Later steps read it as <code>{contextReference(variable.name)}</code>.{replaces && ` Replaces the value set by ${quoted(replaces)}.`}
          </p>
        )}
      </section>
    </li>
  );
}

/**
 * Settings of a Context step (BB-119): up to 10 variables, each a name, a type and a Liquid value. When a user reaches
 * the step the values are worked out in order from the event that brought them and their profile, converted to the
 * type and kept on the entry as `context.<name>` for every later step, even if the profile changes. Shows what earlier
 * Context steps already set, so the values can build on it, and previews the step for a user and an example event.
 */
export function ContextStepPanel({ environmentId, value, onChange, readOnly = false, earlier, canPreview = true }: ContextStepPanelProps) {
  const config = readContext(value);
  const issues = contextIssues(config);
  const setVariable = (index: number, next: ContextVariable) => onChange({ variables: config.variables.map((v, i) => (i === index ? next : v)) });
  const move = (index: number, delta: number) => {
    const variables = [...config.variables];
    const [v] = variables.splice(index, 1);
    variables.splice(index + delta, 0, v!);
    onChange({ variables });
  };
  const setters = new Map((earlier ?? []).map((v) => [v.name, v.from]));

  return (
    <section className="step-panel" aria-label="Context settings">
      <p className="hint">
        Values are worked out once, when a user reaches this step, from the event that brought them here (<code>event_properties</code>) and their profile (<code>user</code>), and kept for this entry: later steps
        see them as they were then, even if the profile changes. Variables are worked out in order, so a later one can use an earlier one.
      </p>
      {earlier && <Earlier earlier={earlier} />}
      <ol className="context-step__variables" aria-label="Variables">
        {config.variables.map((variable, index) => (
          <VariableRow
            // Positions: a name is being typed, and keying by it would remount the row on every key.
            key={index}
            environmentId={environmentId}
            index={index}
            count={config.variables.length}
            variable={variable}
            issues={issues.each[index]!}
            replaces={setters.get(variable.name)}
            readOnly={readOnly}
            set={(next) => setVariable(index, next)}
            move={(delta) => move(index, delta)}
            remove={() => onChange({ variables: config.variables.filter((_, i) => i !== index) })}
          />
        ))}
      </ol>
      {issues.variables && (
        <p className="bb-field__error" role="alert">
          {issues.variables}
        </p>
      )}
      {!readOnly && (
        <div className="filters">
          <Button size="sm" disabled={config.variables.length >= CONTEXT_MAX_VARIABLES} onClick={() => onChange({ variables: [...config.variables, newVariable(config.variables)] })}>
            Add variable
          </Button>
          <span className="hint">
            {config.variables.length} of {CONTEXT_MAX_VARIABLES} variables
          </span>
        </div>
      )}
      <p className="hint">A value that cannot be used — a Liquid error, abort_message, the wrong type, or context over 50 KB — is not updated: the entry keeps its old value, the other variables still update and the user goes on.</p>
      <ContextPreview environmentId={environmentId} config={config} valid={!hasContextIssues(issues)} earlier={earlier ?? []} canPreview={canPreview} />
    </section>
  );
}
